import React, { useEffect } from 'react';
import { useIntl } from "react-intl";
import moment from "moment";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { useQueryClient } from '@tanstack/react-query';
import { BackgroundJob, BackgroundJobStatus, BackgroundJobType } from "@/model";
import { useProfile } from "@/query/profile";
import { bulkTestQueryKeys } from '@/query/bulkTest/keys';
import { sortedBackgroundJobsAtom } from "@/atom/backgroundJob";
import { useAgentId, useWebsocket } from "@/hook";
import { errorToast, successToast } from "@/component/Toast";
import Tooltip from "@/component/Tooltip";
import { Modal } from "@/component/modal";
import { DeleteIcon } from "@/assets/svgs/icons";


export const JOB_MONITOR_MODAL_ID = "job-monitor-modal";

const STATUS_STYLES: Record<BackgroundJobStatus, {background: string, text: string}> = {
    in_progress: { background: "bg-point-green", text: "text-point-green" },
    waiting: { background: "bg-black-500", text: "text-black-500" },
    canceled: { background: "bg-black-500", text: "text-black-500"  },
    error: { background: "bg-point-red", text: "text-point-red" },
    done: { background: "bg-black-500", text: "text-black-500" }
};

const TYPE_LABEL_KEYS: Record<BackgroundJobType, string> = {
    agent: "i00028",
    intent: "i00029",
    typo: "i00030",
    entity: "i00031",
    synonym: "i00032",
    selector: "i00033",
    autocomplete: "i00034",
    autocomplete_delete: "i00063",
    testing: "i00035",
    training: "i00036",
    deploy: "i00037",
    entity_export: "i00038",
    entity_delete: "i00039",
    entity_bulk_insert: "i00040",
    entity_bulk_apply: "i00041"
};

const STATUS_LABEL_KEYS: Record<BackgroundJobStatus, string> = {
    in_progress: "i00042",
    waiting: "i00043",
    canceled: "i00044",
    error: "i00045",
    done: "i00046"
};

const JobMonitorModal: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const websocket = useWebsocket();
    const queryClient = useQueryClient();

    const backgroundJobs = useAtomValue(sortedBackgroundJobsAtom);
    const { data: user } = useProfile();

    const secondsToHumanize = (seconds: number) => {
        return moment.duration(seconds, "seconds").locale(intl.locale).humanize();
    };

    const onClickDelete = (job: BackgroundJob) => {
        if (["in_progress", "waiting"].includes(job.status)) {
            websocket.cancelBackgroundJob(job);
        } else {
            websocket.deleteBackgroundJob(job);
        }
    };

    useEffect(() => {
        if (intl.locale === "en") {
            return;
        }
        import(`moment/locale/${intl.locale}`);
    }, [intl.locale]);

    useEffect(() => {
        if (!user) {
            return;
        }
        websocket.connect(agentId, user, { transports: ["websocket"] });
        // eslint-disable-next-line
    }, [user, agentId]);

    useEffect(() => {
        const receiveBackgroundJobEnded = (job: BackgroundJob) => {
            if (["entity_bulk_insert", "entity_bulk_apply"].includes(job.type)) {
                return;
            }

            const jobLabel = intl.formatMessage({ id: TYPE_LABEL_KEYS[job.type] });
            if (job.status === "done") {
                successToast(intl.formatMessage({ id: "i00061" }, { job: jobLabel }));
            } else if (job.status === "error") {
                errorToast(intl.formatMessage({ id: "i00062" }, { job: jobLabel }));
            }

            if (job.type === "testing") {
                queryClient.invalidateQueries({
                    queryKey: bulkTestQueryKeys.pageAll(agentId),
                    exact: false
                });
            }
        };

        websocket.addEventListener("background_job_ended", receiveBackgroundJobEnded);
        return () => {
            websocket.removeEventListener("background_job_ended", receiveBackgroundJobEnded);
        };
    }, [intl, websocket, queryClient, agentId]);

    return (
        <Modal id={JOB_MONITOR_MODAL_ID}
               size="xl"
               title={
                    <div className="flex items-center gap-2">
                        <div className="font-[500] text-[16px] leading-[1.5] text-black-500">
                            {intl.formatMessage({ id: "i00027" })}
                        </div>
                        <JobStatusMark />
                    </div>
                }
        >
            <div role="table"
                 className="h-full min-h-0 w-full mt-6 grid-table-job-monitor rounded-[6px] shadow-table shadow-black-200 bg-white"
            >
                <div role="rowgroup"
                     className="grid grid-cols-subgrid col-span-full text-black-500 text-[14px] font-[700] leading-[1.5] border-b border-black-200 h-table-row"
                >
                    <div role="row"
                         className="grid grid-cols-subgrid col-span-full items-center min-h-[44px] border-b-0"
                    >
                    <div role="columnheader"
                         className="pl-[25px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00047" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00048" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00049" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00050" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00051" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00052" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00053" })}
                    </div>
                    <div role="columnheader"
                         className="pl-[15px] text-start flex items-center"
                    >
                        {intl.formatMessage({ id: "i00054" })}
                    </div>
                    <div role="columnheader"
                         className="flex items-center"
                    />
                    </div>
                </div>

                <div role="rowgroup"
                     className="grid grid-cols-subgrid col-span-full auto-rows-max text-black-500 text-[14px] font-[400] leading-[1.5] overflow-y-auto overflow-x-hidden"
                >
                    {backgroundJobs.map((job, index) => (
                        <div key={index}
                             role="row"
                             className="grid grid-cols-subgrid col-span-full items-center min-h-[44px] border-b border-black-150 bg-transparent"
                        >
                            <div role="cell"
                                 className="pl-[25px] text-start flex items-center"
                            >
                                {intl.formatMessage({ id: TYPE_LABEL_KEYS[job.type] })}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {job.count_current} / {job.count_total}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {moment.utc(job.created_at).local().format("YYYY-MM-DD HH:mm:ss")}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {secondsToHumanize(job.elapsed_seconds)}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {job.remain_seconds > 0 ? secondsToHumanize(job.remain_seconds) : "-"}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {job.processing_seconds > 0 ? secondsToHumanize(job.processing_seconds) : "-"}
                            </div>
                            <div role="cell"
                                 className="pl-[15px] text-start flex items-center"
                            >
                                {job.download_link && (
                                    <a target="_blank"
                                       rel="noreferrer"
                                       className="hover:underline"
                                       href={job.download_link}
                                    >
                                        {intl.formatMessage({ id: "i00065" })}
                                    </a>
                                )}
                            </div>
                            <div role="cell"
                                 className={classNames(
                                     "pl-[15px] flex items-center gap-0.5 text-center",
                                     STATUS_STYLES[job.status].text
                                 )}
                            >
                                <Tooltip message={job.status_detail ?? ""}
                                         placement="right"
                                         disable={job.status !== "error" && job.status !== "in_progress"}
                                         tooltipClassName="w-[200px] text-start"
                                >
                                    {intl.formatMessage({ id: STATUS_LABEL_KEYS[job.status] })}
                                </Tooltip>
                            </div>
                            <div role="cell"
                                 className="flex items-center justify-center"
                            >
                                <button onClick={() => onClickDelete(job)}>
                                    <DeleteIcon className="size-4.5 stroke-black-300" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export const JobStatusMark: React.FC = () => {
    const backgroundJobs = useAtomValue(sortedBackgroundJobsAtom);

    const firstJobStatus = backgroundJobs[0]?.status;

    return (
        <div className={classNames(
                 "size-2.5 rounded-full",
                 firstJobStatus ? STATUS_STYLES[firstJobStatus].background : "bg-black-500",
                 firstJobStatus === "in_progress" && "animate-blink"
             )}
        />
    );
};

export default JobMonitorModal;
